import { LitElement, html, css, ref, createRef } from '@hh/global.js'

class ThreeTeaserBlock extends LitElement {
  static properties = {
    _ctaSelector: {
      type: String,
      attribute: 'cta-selector',
    },
    _featuredClass: {
      type: String,
      attribute: 'feature-class',
    },
    _itemSelector: {
      type: String,
      attribute: 'item-selector',
    },
    _paddingSelector: {
      type: String,
      attribute: 'padding-selector',
    },
    _rootSelector: {
      type: String,
      attribute: 'root-selector',
    },
  }

  $slider = createRef()

  constructor() {
    super()
    this._featuredChangeTimeout = null
    this.render()
  }

  _getItems() {
    const slot = this.renderRoot?.querySelector('slot')
    if (!slot) {
      return false
    }

    return slot.assignedElements({ flatten: true })
  }

  _setActiveElementObserver() {
    const slider = this.$slider.value
    if (!slider) {
      return
    }

    const observer = new MutationObserver((mutationList) => {
      mutationList.forEach((mutation) => {
        const { type, attributeName, target } = mutation

        if (
          type === 'attributes' &&
          ['class', 'style'].indexOf(attributeName) > -1
        ) {
          const hasFeaturedClass = target.classList.contains(
            this._featuredClass
          )
          const itemCta = target.querySelector(this._ctaSelector)
          const hasPadding = target.closest(this._paddingSelector)

          if (!itemCta || !hasPadding) {
            return
          } else if (hasFeaturedClass) {
            itemCta.setAttribute('inverse', '')
          } else {
            itemCta.removeAttribute('inverse', '')
          }
        }
      })
    })

    if (this._items) {
      this._items.forEach((item) => {
        observer.observe(item, { attributes: true })
      })
    }
  }

  _addEventListeners() {
    const slider = this.$slider.value
    if (slider) {
      if ('onscrollend' in window) {
        slider.addEventListener('scrollend', () => {
          if (window.innerWidth > 1023) {
            return
          }

          const index = this._getFeaturedItemIndexAfterScroll()
          this._setFeaturedItemClass(this._items[index])
        })
      } else {
        slider.addEventListener('scroll', this._handleTouchEnd.bind(this))
      }
      setTimeout(() => {
        window.addEventListener('resize', () => {
          if (window.innerWidth !== this._initialWidth) {
            this._centerAndResize()
          }
        })
      }, 500)
    }

    if (this._items) {
      this._items.forEach((_item) => {
        _item.addEventListener('mouseover', this._handleMouseover.bind(this))
        _item.addEventListener('click', this._handleCardTouch.bind(this))
      })
    }
  }

  _handleMouseover(ev) {
    if (window.innerWidth < 1024) {
      return
    }

    const { currentTarget } = ev
    this._setFeaturedItemClass(currentTarget)
  }

  _handleTouchEnd() {
    if (window.innerWidth > 1023) {
      return
    }

    const slider = this.$slider.value
    if (!slider) {
      return
    }

    clearTimeout(this._featuredChangeTimeout)
    this._featuredChangeTimeout = setTimeout(() => {
      const index = this._getFeaturedItemIndexAfterScroll()
      this._setFeaturedItemClass(this._items[index])
      clearTimeout(this._featuredChangeTimeout)
      this._featuredChangeTimeout = null
    }, 50)
  }

  _handleCardTouch({ currentTarget }) {
    if (window.innerWidth > 1023) {
      return
    }

    const slider = this.$slider.value
    if (!slider) {
      return
    }

    clearTimeout(this._featuredChangeTimeout)
    this._featuredChangeTimeout = null
    const index = parseInt(currentTarget.dataset?.index || 0)
    if (!this._items[index]) {
      return
    }

    const scrollTo = this._items[index].scrollWidth * index
    slider.scrollTo({
      behavior: 'smooth',
      left: scrollTo,
    })
    this._setFeaturedItemClass(this._items[index])
  }

  _centerAndResize() {
    if (this._items[1]) {
      this._items[1].classList.add(this._featuredClass)
    }
    setTimeout(() => {
      this._centerTeaserElements()
    }, 150)
    setTimeout(() => {
      this._setMinHeight()
    }, 250)
  }

  _getFeaturedItemIndexAfterScroll() {
    const slider = this.$slider.value
    if (!slider) {
      return
    }

    const currentScroll = slider.scrollLeft
    const columnSeparation = parseInt(
      getComputedStyle(slider)?.columnGap?.replace('px', '') || 0
    )
    const adjustment = window.innerWidth < 1023 ? 6 : 2
    const featuredItem = this._items[0]
    const scrollAmount =
      featuredItem.clientWidth - columnSeparation * 3 - adjustment
    const index =
      currentScroll < scrollAmount
        ? 0
        : Math.floor(currentScroll / scrollAmount)

    if (isNaN(index) || index < 0 || typeof index !== 'number') {
      return
    }

    const featuredItemIndex =
      index >= this._items.length ? this._items.length - 1 : index
    return featuredItemIndex
  }

  _centerTeaserElements() {
    const slider = this.$slider.value
    if (!slider) {
      return
    }

    if (window.innerWidth > 1023) {
      return
    } else if (!this._items || !this._items[1]) {
      return
    }

    const scrollTo = this._items[1].scrollWidth
    slider.scrollTo({
      behavior: 'instant',
      left: scrollTo,
    })
  }

  _clearFeaturedClass() {
    this._items.forEach((_item, i) => {
      _item.classList.remove(this._featuredClass)
    })
  }

  _setFeaturedItemClass(item) {
    this._items.forEach((_item, i) => {
      if (i === parseInt(item.dataset?.index ?? 0)) {
        _item.classList.add(this._featuredClass)
        return
      }
      _item.classList.remove(this._featuredClass)
    })
  }

  _outerHeight(element) {
    const height = element.offsetHeight
    const style = window.getComputedStyle(element)

    return ['top', 'bottom']
      .map((side) => parseInt(style[`margin-${side}`]), 10)
      .reduce((total, side) => total + side, height)
  }

  _setMinHeight() {
    const featuredElement = this.querySelector('.' + this._featuredClass)
    if (!featuredElement) {
      return
    }

    this.style.removeProperty('--hh-min-height')
    const featuredElementHeight = this._outerHeight(featuredElement) + 'px'
    this.style.setProperty('--hh-min-height', featuredElementHeight)
  }

  firstUpdated() {
    this._items = this._getItems()
    if (!this._items) {
      return
    }

    this._setActiveElementObserver()
    setTimeout(() => {
      this._centerAndResize()
      this._initialWidth = window.innerWidth
    }, 500)

    this._addEventListeners()
  }

  render() {
    return html`<ul class="teaser-block" ${ref(this.$slider)}>
      <slot name="item"></slot>
      <li class="teaser-block__extra-item" aria-hidden="true"></li>
    </ul>`
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    :host ::slotted(*) {
      list-style-type: none;
    }

    .teaser-block {
      align-items: var(--hh-v-align-items, stretch);
      display: flex;
      column-gap: var(--hh-column-gap, var(--hh-spacing-md, 1.6rem));
      flex-wrap: nowrap;
      grid-auto-flow: column;
      justify-content: start;
      list-style: none;
      margin: 0;
      min-height: var(--hh-min-height, auto);
      min-width: var(--hh-min-width, 100vw);
      overflow-x: auto;
      padding: 0;
      scroll-behavior: smooth;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
      touch-action: manipulation;
    }

    .teaser-block::-webkit-scrollbar {
      display: none;
    }

    .teaser-block__extra-item {
      margin: 0;
      padding: 0;
    }

    @supports (-webkit-touch-callout: none) {
      .teaser-block__extra-item {
        height: 1px;
        padding-right: 1px;
      }
    }

    @media screen and (min-width: 1024px) {
      .teaser-block {
        justify-content: center;
      }
    }
  `
}

customElements.define('hh-three-teaser-block', ThreeTeaserBlock)
